import { ERROR_MESSAGES, REQUEST } from '../../consts';
import {
  MonetaryValueType,
  PageType,
  StoryType,
  UpdateTeamCoverMutation,
  UpdateTeamCoverMutationVariables,
  UpdateTeamMutation,
  UpdateTeamMutationVariables,
} from '../../../types/graphql';
import { apiActions, httpActions } from '..';
import client, { hasUnauthenticatedError } from '../../apolloClient';
import {
  getCurrencyCode,
  getTeamFundraisingTarget,
  getTeamGuid,
} from '../../selectors/apiSelectors';
import { mapStory, redirectByResponseStatus, uploadImage } from '../../utils';

import { Action } from 'redux';
import { Auth } from '@justgiving/auth';
import { IAppState } from '../../types/state.d';
import { IEditTeamFormProps } from '../../containers/EditTeam/EditTeam.d';
import { ThunkDispatch } from 'redux-thunk';
import UpdateCoverMutation from '../../queries/updateTeamCoverMutation.gql';
import UpdateMutation from '../../queries/updateTeamMutation.gql';
import config from '../../config';
import { isDataURL } from '@justgiving/image-editor';

const editTeam = ({
  teamName,
  story,
  heroImage,
  fundraisingTarget,
}: IEditTeamFormProps) => async (
  dispatch: ThunkDispatch<IAppState, Auth, Action>,
  getState: () => IAppState,
) => {
  try {
    dispatch(apiActions.submitTeamDataRequestMade());
    const state = getState();
    const teamGuid = getTeamGuid(state);

    if (!teamGuid) {
      throw new Error('Team guid not found');
    }

    const currencyCode = getCurrencyCode(state);
    const existingTarget = getTeamFundraisingTarget(state);
    const pageStory = await mapStory(story);

    let coverImageId;
    const uploadImageResourceName = REQUEST.UPLOAD_IMAGE;
    if (heroImage && isDataURL(heroImage)) {
      dispatch(httpActions.httpRequestMade(uploadImageResourceName));
      coverImageId = await uploadImage(heroImage);
      dispatch(httpActions.httpRequestSuccess(uploadImageResourceName));
      dispatch(apiActions.saveUploadedCoverPhoto(coverImageId));
    } else if (heroImage) {
      coverImageId = heroImage;
    }

    const { errors: updateDetailErrors } = await client.mutate<
      UpdateTeamMutation,
      UpdateTeamMutationVariables
    >({
      mutation: UpdateMutation,
      variables: {
        input: {
          id: teamGuid,
          type: PageType.Team,
          title: teamName,
          target:
            existingTarget !== fundraisingTarget
              ? {
                  currencyCode: currencyCode,
                  value: fundraisingTarget ?? 0,
                  type: MonetaryValueType.Whole,
                }
              : undefined,
          story: pageStory
            ? {
                content: pageStory,
                type: StoryType.RichStoryV1Json,
              }
            : undefined,
        },
      },
    });

    if (updateDetailErrors && hasUnauthenticatedError(updateDetailErrors)) {
      redirectByResponseStatus(401);
    }

    if (updateDetailErrors) {
      throw new Error(ERROR_MESSAGES.EDIT_TEAM_DETAILS);
    }

    const { errors: updateCoverErrors } = await client.mutate<
      UpdateTeamCoverMutation,
      UpdateTeamCoverMutationVariables
    >({
      mutation: UpdateCoverMutation,
      variables: {
        input: {
          pageId: teamGuid,
          pageType: PageType.Team,
          media: {
            imageMedia: {
              url: coverImageId
                ? `${config.imageAPIBaseURL}/${coverImageId}`
                : `${config.imageAPIBaseURL}/${config.defaultCoverPhoto}`,
            },
          },
        },
      },
    });

    if (updateCoverErrors) {
      throw new Error(ERROR_MESSAGES.UPADTE_TEAM_COVER);
    }

    dispatch(
      apiActions.submitTeamDataRequestSuccess({
        teamName,
        story,
        heroImage,
        fundraisingTarget,
      }),
    );
  } catch (error) {
    dispatch(apiActions.submitTeamDataRequestError((error as Error).message));
    throw error;
  }
};

export default editTeam;
